/**
 * O que vai para o palco dos dados 3D, sem depender da física nem do three.js
 * (este módulo é carregado com a mesa; o resto só quando chega uma rolagem).
 */
import { serverNow } from '../serverClock.js';
import { diceAnimation, type GameRules, type PolyhedralSides } from '../shared.js';
import type { LabelSet } from './dieMesh.js';

/** Raio base do dado em unidades de cena. */
export const DIE_RADIUS = 0.55;

export type StageDie = {
  sides: PolyhedralSides;
  value: number;
  labels: LabelSet;
  /** posição do dado em `RollResult.dice` (o d100 vira dois dados com a mesma origem) */
  source?: number;
};

/**
 * A mesa, em unidades de cena: metade da largura e da profundidade; mão e alvo do
 * arremesso; `ceiling`, a altura do teto (o dado não chega perto da câmera);
 * `cameraHeight`, a altura da câmera, que olha de cima para o meio da mesa (as
 * paredes da física seguem o que ela enxerga).
 */
export type TableSpec = {
  halfWidth: number;
  halfDepth: number;
  hand: { x: number; z: number };
  aim: { x: number; z: number };
  ceiling: number;
  cameraHeight: number;
};

/** Quantos dados de uma rolagem aparecem (o resto fica só no chat). */
export const MAX_STAGE_DICE = 20;

/**
 * Dados da rolagem que entram no palco: os poliédricos e o d100 (dezenas e
 * unidades, como dois d10). O d10 comum vai de 0 a 9, como os dados de verdade.
 */
export function stageDice(dice: readonly { sides: number; value: number }[], max = MAX_STAGE_DICE): StageDie[] {
  const out: StageDie[] = [];
  for (const [source, die] of dice.entries()) {
    if (out.length >= max) break;
    if (die.sides === 100) {
      // o d100 são dois dados: sem as duas vagas, não entra pela metade
      if (out.length + 2 > max) break;
      const value = ((die.value - 1) % 100) + 1;
      const tens = Math.floor((value % 100) / 10);
      const units = value % 10;
      out.push({ sides: 10, value: tens === 0 ? 10 : tens, labels: 'd10tens', source }, { sides: 10, value: units === 0 ? 10 : units, labels: 'd10units', source });
    } else if (die.sides === 10) out.push({ sides: 10, value: die.value, labels: 'd10units', source });
    else if (die.sides === 4 || die.sides === 6 || die.sides === 8 || die.sides === 12 || die.sides === 20) out.push({ sides: die.sides, value: die.value, labels: 'value', source });
  }
  return out;
}

/** Rolagens mais velhas que isto não animam (histórico ao entrar, reconexão). */
export const FRESH_MS = 8000;

/**
 * Os dados 3D estão ligados nesta tela: na minha escolha (Configurações → Dados) e na regra da mesa
 * (Regras de jogo → Dados).
 */
export function diceOn(settings: { show3DDice: boolean }, room: { gameRules?: GameRules | undefined } | null | undefined): boolean {
  return settings.show3DDice && diceAnimation(room);
}

/**
 * A rolagem vai animar nesta tela, a julgar pelo que se sabe na chegada: os dados ligados, algum dado
 * com forma e a rolagem recente. O resultado dela fica guardado até os dados pararem
 * (docs/spec/dice-3d.md, "Resultado depois da animação"); o que só se descobre depois (sem WebGL,
 * mesa pequena, rolagens demais) solta o resultado pela camada dos dados (DiceLayer).
 */
export function animatesRoll(
  roll: { dice: readonly { sides: number; value: number }[]; createdAt: number },
  settings: { show3DDice: boolean },
  room: { gameRules?: GameRules | undefined } | null | undefined,
): boolean {
  return diceOn(settings, room) && stageDice(roll.dice).length > 0 && serverNow() - roll.createdAt < FRESH_MS;
}
